import { Children, cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { cx } from "@/lib/cx";

interface RevealStaggerProps {
  children: ReactNode;
  step?: number;
  delay?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * One IntersectionObserver per group. When the wrapper enters view,
 * `data-revealed="true"` flips on it; the `.reveal-stagger > *` CSS
 * rule applies a `--stagger-delay`-driven transition on each direct
 * child. Per-child delay is computed from the child's index in the
 * children array and injected via inline style (`--stagger-delay:
 * X * step ms`).
 *
 * The wrapper is a single `<div>` whose className/style come from
 * the consumer — so a consumer with `<div className="grid">` wraps
 * directly into `<RevealStagger className="grid">` without adding
 * DOM depth. Children must be direct: deeper-nested cards are NOT
 * staggered.
 */
export default function RevealStagger({
  children,
  step = 60,
  delay = 0,
  className,
  style,
}: RevealStaggerProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const t = window.setTimeout(() => setRevealed(true), delay);
            obs.unobserve(e.target);
            return () => window.clearTimeout(t);
          }
        }
        return undefined;
      },
      { threshold: 0.12 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [delay]);

  const childArray = Children.toArray(children);

  return (
    <div
      ref={ref}
      className={cx("reveal-stagger", className)}
      data-revealed={revealed ? "true" : undefined}
      style={style}
    >
      {childArray.map((child, i) => {
        if (!isValidElement(child)) return child;
        const childEl = child as ReactElement<{ style?: CSSProperties }>;
        const existingStyle = childEl.props.style ?? {};
        return cloneElement(childEl, {
          key: childEl.key ?? i,
          style: { ...existingStyle, ["--stagger-delay" as string]: `${i * step}ms` },
        });
      })}
    </div>
  );
}
